import { FC, useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetFooter, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { driverZ, DriverStatus, driverStatuses, DriverZ } from '@libs/schemas';
import { PatternFormat } from 'react-number-format';
import { cn } from '@/lib/utils';
import { trpc } from 'src/trpc';
import { useToast } from 'src/components/useToast';
import { User } from 'lucide-react';

interface DriverSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DriverSheet: FC<DriverSheetProps> = ({ isOpen, onClose }) => {
  const { successToast, errorToast } = useToast();
  const utils = trpc.useUtils();
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const form = useForm<DriverZ>({
    resolver: zodResolver(driverZ),
    defaultValues: {
      name: { first: '', last: '' },
      email: '',
      phone: '',
      status: 'available' as DriverStatus,
    },
    mode: 'onChange',
  });

  const createDriverMutation = trpc.admin.driver.createDriver.useMutation({
    onSuccess: () => {
      successToast({
        title: 'Driver created',
        description: 'New driver has been successfully created',
      });
      utils.admin.driver.getAllDrivers.invalidate();
      form.reset();
      setEmailError(null);
      setPhoneError(null);
      onClose();
    },
    onError: (error) => {
      // Check for specific duplicate key errors
      if (error.message.includes('idx_drivers_email')) {
        setEmailError('This email is already in use by another driver');
        errorToast({
          title: 'Failed to create driver',
          description: 'This email is already in use by another driver',
        });
      } else if (error.message.includes('idx_drivers_phone')) {
        setPhoneError('This phone number is already in use by another driver');
        errorToast({
          title: 'Failed to create driver',
          description: 'This phone number is already in use by another driver',
        });
      } else {
        errorToast({
          title: 'Failed to create driver',
          description: error.message || 'An error occurred while creating the driver',
        });
      }
    },
  });

  const onSubmit = async (values: DriverZ) => {
    setEmailError(null);
    setPhoneError(null);
    createDriverMutation.mutate(values);
  };

  const handleSubmitClick = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      const values = form.getValues();
      onSubmit(values);
    }
  };

  const handleClose = () => {
    if (!createDriverMutation.isPending) {
      form.reset();
      setEmailError(null);
      setPhoneError(null);
      onClose();
    }
  };

  // Helper to prepare phone value for display in the input
  const preparePhoneForInput = (phone: string = '') => {
    // Strip any non-digits and the +1 prefix if present
    const digitsOnly = phone.replace(/\D/g, '');

    // If it starts with 1 (country code), remove it for the input
    if (digitsOnly.startsWith('1') && digitsOnly.length > 1) {
      return digitsOnly.substring(1);
    }

    return digitsOnly;
  };

  // Reset field-specific errors when values change
  useEffect(() => {
    const subscription = form.watch((_value, { name }) => {
      if (name === 'email') {
        setEmailError(null);
      } else if (name === 'phone') {
        setPhoneError(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full"
      >
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          {/* Header */}
          <SheetHeader className="border-b bg-white px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-orange-100 p-2">
                <User className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <SheetTitle className="text-xl font-semibold text-gray-900">
                  Create New Driver
                </SheetTitle>
              </div>
            </div>
          </SheetHeader>

          {/* Content Area - Scrollable */}
          <div className="overflow-y-auto px-6 py-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} id="driver-form" className="space-y-8">
                {/* Driver Details Section */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Driver Details</h3>
                    <p className="text-sm text-muted">Basic driver information</p>
                  </div>

                  <div className="grid grid-cols-1 gap-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="name.first"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-muted">
                              First Name
                            </FormLabel>
                            <FormControl>
                              <Input placeholder="Enter first name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="name.last"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-muted">
                              Last Name
                            </FormLabel>
                            <FormControl>
                              <Input placeholder="Enter last name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-muted">Email</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter email address" type="email" {...field} />
                          </FormControl>
                          {emailError && (
                            <p className="text-sm font-medium text-destructive">{emailError}</p>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-muted">Phone</FormLabel>
                          <FormControl>
                            <PatternFormat
                              placeholder="+1 (305)-123-4567"
                              format="+1 (###)-###-####"
                              customInput={Input}
                              onValueChange={(values) => {
                                field.onChange(values.value ? `+1${values.value}` : '');
                              }}
                              value={preparePhoneForInput(field.value)}
                              onBlur={field.onBlur}
                              className={cn(
                                'flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                              )}
                            />
                          </FormControl>
                          {phoneError && (
                            <p className="text-sm font-medium text-destructive">{phoneError}</p>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <Separator />

                {/* Status Section */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Status</h3>
                    <p className="text-sm text-muted">Driver availability status</p>
                  </div>

                  <div className="grid grid-cols-1 gap-6">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-muted">Status</FormLabel>
                          <FormControl>
                            <Select
                              value={field.value}
                              onValueChange={(value) => {
                                field.onChange(value as DriverStatus);
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                              <SelectContent>
                                {driverStatuses.map((status) => (
                                  <SelectItem key={status} value={status}>
                                    {status.charAt(0).toUpperCase() +
                                      status.slice(1).replace('-', ' ')}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </form>
            </Form>
          </div>

          {/* Footer */}
          <SheetFooter className="border-t bg-gray-50 px-6 py-4">
            <div className="flex w-full items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={createDriverMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmitClick}
                disabled={createDriverMutation.isPending}
                className={cn(
                  'rounded-lg transition-all shadow-sm hover:shadow',
                  createDriverMutation.isPending
                    ? 'bg-primary/50 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary/90',
                )}
              >
                {createDriverMutation.isPending ? 'Creating...' : 'Create Driver'}
              </Button>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
